import { useMutation } from "@tanstack/react-query";
import { Bot, Search } from "lucide-react";
import { useState } from "react";

import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { FieldError } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { askInternalDocs, searchInternalDocs } from "./docs-api";
import type { OperationsNavigate } from "./navigation";

function AskDocs({ navigate }: { navigate: OperationsNavigate }) {
  const [question, setQuestion] = useState("");
  const ask = useMutation({ mutationFn: askInternalDocs });
  return (
    <Dialog>
      <DialogTrigger render={<Button variant="outline" />}>
        <Bot /> Ask Docs
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Ask Docs</DialogTitle>
          <DialogDescription>
            Answers use only pages you can currently read and include their sources.
          </DialogDescription>
        </DialogHeader>
        <DialogForm
          aria-label="Ask Docs"
          onSubmit={(event) => {
            event.preventDefault();
            if (question.trim().length >= 2) ask.mutate({ question });
          }}
        >
          <DialogBody>
            <label className="sr-only" htmlFor="ask-docs-question">
              Question
            </label>
            <Textarea
              className="min-h-28"
              id="ask-docs-question"
              maxLength={1_000}
              onChange={(event) => {
                setQuestion(event.target.value);
              }}
              placeholder="What is our process for…?"
              value={question}
            />
            {ask.error ? <FieldError>{ask.error.message}</FieldError> : null}
            {ask.data ? (
              <section className="space-y-4 rounded-sm border bg-muted/30 p-4" aria-live="polite">
                <p className="whitespace-pre-wrap text-sm leading-6">{ask.data.answer}</p>
                {ask.data.sources.length > 0 ? (
                  <div>
                    <h3 className="mb-2 text-xs font-semibold text-muted-foreground">Sources</h3>
                    <div className="flex flex-wrap gap-2">
                      {ask.data.sources.map((source) => (
                        <Button
                          key={source.pageId}
                          onClick={() => {
                            navigate(`/docs/${source.pageId}`);
                          }}
                          size="sm"
                          type="button"
                          variant="outline"
                        >
                          {source.title}
                        </Button>
                      ))}
                    </div>
                  </div>
                ) : null}
              </section>
            ) : null}
          </DialogBody>
          <DialogFooter>
            <Button disabled={ask.isPending || question.trim().length < 2} type="submit">
              <Bot /> {ask.isPending ? "Reading…" : "Ask"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

export function DocsSearch({ navigate }: { navigate: OperationsNavigate }) {
  const [query, setQuery] = useState("");
  const search = useMutation({ mutationFn: searchInternalDocs });
  return (
    <section className="bg-card" aria-label="Search documentation">
      <div className="flex flex-col gap-2 sm:flex-row">
        <form
          className="flex min-w-0 flex-1 gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (query.trim().length >= 2) search.mutate({ query, limit: 10 });
          }}
        >
          <label className="sr-only" htmlFor="docs-search">
            Search documentation
          </label>
          <Input
            className="flex-1"
            id="docs-search"
            maxLength={500}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
            placeholder="Search internal docs"
            value={query}
          />
          <Button disabled={search.isPending || query.trim().length < 2} type="submit">
            <Search /> <span className="hidden sm:inline">Search</span>
          </Button>
        </form>
        <AskDocs navigate={navigate} />
      </div>
      {search.error ? <FieldError className="mt-3">{search.error.message}</FieldError> : null}
      {search.data ? (
        <div className="mt-3 space-y-1 border-t pt-3" aria-live="polite">
          <p className="mb-2 text-xs text-muted-foreground">
            {search.data.results.length} result{search.data.results.length === 1 ? "" : "s"} ·{" "}
            {search.data.mode}
          </p>
          {search.data.results.length === 0 ? (
            <p className="py-3 text-sm text-muted-foreground">No accessible pages matched.</p>
          ) : (
            search.data.results.map((result) => (
              <button
                className="block w-full rounded-sm px-3 py-2 text-left hover:bg-muted focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)]"
                key={result.pageId}
                onClick={() => {
                  navigate(`/docs/${result.pageId}`);
                }}
                type="button"
              >
                <span className="block text-sm font-semibold">{result.title}</span>
                <span className="block truncate text-xs text-muted-foreground">{result.path}</span>
                {result.snippet ? (
                  <span className="mt-1 line-clamp-2 block text-sm text-muted-foreground">
                    {result.snippet}
                  </span>
                ) : null}
              </button>
            ))
          )}
        </div>
      ) : null}
    </section>
  );
}
